import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

import { getUserHomePreferences } from "../services/userLibraryService";

import { useAuth } from "./AuthContext";

interface UserPreferencesContextType {
  hiddenSectionIds: Set<string>;
  setHiddenSectionIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  refreshPreferences: () => Promise<void>;
  isLoadingPreferences: boolean;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(
  undefined,
);

export const UserPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [hiddenSectionIds, setHiddenSectionIds] = useState<Set<string>>(
    new Set(),
  );
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);

  const refreshPreferences = useCallback(async () => {
    if (!user) {
      setHiddenSectionIds(new Set());
      return;
    }

    setIsLoadingPreferences(true);
    try {
      const prefs = await getUserHomePreferences(user.id);
      const hidden = new Set(
        prefs.filter((p) => !p.isVisible).map((p) => p.sectionId),
      );
      setHiddenSectionIds(hidden);
    } catch (error) {
      console.error("Failed to load user preferences", error);
    } finally {
      setIsLoadingPreferences(false);
    }
  }, [user]);

  useEffect(() => {
    refreshPreferences();
  }, [refreshPreferences]);

  return (
    <UserPreferencesContext.Provider
      value={{
        hiddenSectionIds,
        setHiddenSectionIds,
        refreshPreferences,
        isLoadingPreferences,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error(
      "useUserPreferences must be used within a UserPreferencesProvider",
    );
  }
  return context;
};
